import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../../../context/ThemeContext";
import { serverURL, postData, getData } from "../../../../services/Fetchnodeservices";

// ─── ICONS ──────────────────────────────────────────────────────
const UploadIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M12 3v12m0 0-3-3m3 3 3-3M5 21h14" />
    <rect x="3" y="15" width="18" height="5" rx="1" />
  </svg>
);

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ImageIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
    <circle cx="8.5" cy="8.5" r="1.5"/>
    <polyline points="21 15 16 10 5 21"/>
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const XIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ─── DOCUMENT PREVIEW (Theme-Aware) ────────────────────────────
const DocumentPreview = ({ picName, label, theme }) => {
  const [imageError, setImageError] = useState(false);
  const getCleanFilename = (picPath) => {
    if (!picPath || picPath.trim() === "" || picPath === "NULL" || picPath === "null") return null;
    if (picPath.includes('/')) return picPath.split('/').pop();
    return picPath;
  };
  const filename = getCleanFilename(picName);
  const imageUrl = filename ? `${serverURL}/uploads/${filename}` : null;

  if (!imageUrl || imageError) {
    return (
      <div style={{
        width: "80px",
        height: "80px",
        borderRadius: "10px",
        background: theme.surfaceLighter,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        border: `1px solid ${theme.border}`,
        gap: "4px"
      }}>
        <span style={{ color: theme.textMuted }}><ImageIcon /></span>
        <span style={{ fontSize: "8px", color: theme.textMuted }}>No Image</span>
      </div>
    );
  }
  return (
    <img
      src={imageUrl}
      alt={label}
      onError={() => {
        console.log(`❌ Failed to load image: ${imageUrl}`);
        setImageError(true);
      }}
      style={{
        width: "80px",
        height: "80px",
        borderRadius: "10px",
        objectFit: "cover",
        border: `1px solid ${theme.border}`,
        background: theme.surfaceLighter
      }}
    />
  );
};

// ─── MAIN COMPONENT (Theme-Aware) ──────────────────────────────
const AddNewDriver = ({ onBack }) => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const location = useLocation();
  const navigate = useNavigate();
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeSection, setActiveSection] = useState("contact");
  const [loading, setLoading] = useState(false);

  // ─── FORM DATA ───────────────────────────────────────────────
  const [formData, setFormData] = useState({
    id: "", old_id: "", name: "", first_name: "", last_name: "", pre: "", gender: "",
    contact: "", primary_number: "", secondary_number: "", whatsapp_number: "",
    email: "", password: "", address: "", permanent_address: "", city: "", state: "", postal: "",
    latitude: "", longitude: "", date_of_birth: "", blood_group: "", language: "",
    father_name: "", heighest_education: "",
    bank_name: "", bank_branch: "", account_holder: "", account_number: "", ifsc: "",
    bank_mobile: "", upi: "", pan_card: "",
    status: "", paid: "", open_status: "", is_ready: "", hide: "",
    wallet: "", cash_inhand: "", cash_limit: "", cash_limit_cheque: "", is_cash_limit_cheque: "",
    tshirt_size: "", tshirt_given: "", bag_given: "", starting_point: "",
    vl_register_number: "", dl_number: "",
    is_verify_personalinfo: "", is_verify_docinfo: "", is_verify_vehicle: "",
    is_verify_bankinfo: "", is_verify_emergency: "",
    remarks_personalinfo: "", remarks_docinfo: "", remarks_vehicleinfo: "",
    remarks_bankinfo: "", remarks_emergencyinfo: "",
    doc_verification: {
      aadhaar: { status: 0, remarks: "" },
      pan: { status: 0, remarks: "" },
      dl: { status: 0, remarks: "" },
    },
    family_number_one: "", family_number_two: "",
    subject: "", details: "", refer_by: "", myreferal: "", referral_code: "",
    aadhaar_number: "", pan_number: "",
    created_by_id: "", created_at: "", updated_at: "", rd: "",
    sessionid: "", login_session: "", fcm_token: "",
  });

  const [documents, setDocuments] = useState({
    pic: null, pic_existing: "",
    aadhar_front: null, aadhar_front_existing: "",
    aadhar_back: null, aadhar_back_existing: "",
    pan_card: null, pan_card_existing: "",
    bank_pass: null, bank_pass_existing: "",
    cancel_cheque: null, cancel_cheque_existing: "",
    dl_front: null, dl_front_existing: "",
    dl_back: null, dl_back_existing: "",
    rc_front: null, rc_front_existing: "",
    rc_back: null, rc_back_existing: "",
    resume: null, resume_existing: "",
  });

  const [filledSections, setFilledSections] = useState({
    contact: false, personalInfo: false, documents: false, bankDetails: false,
    workDuty: false, verification: false, familyEmergency: false, meta: false,
  });

  const sections = [
    { id: "contact", label: "Contact", icon: "📞" },
    { id: "personalInfo", label: "Personal Info", icon: "👤" },
    { id: "documents", label: "Documents", icon: "📄" },
    { id: "bankDetails", label: "Bank Details", icon: "🏦" },
    { id: "workDuty", label: "Work & Duty", icon: "⚙️" },
    { id: "verification", label: "Verification", icon: "✅" },
    { id: "familyEmergency", label: "Family/Emergency", icon: "👨‍👩‍👧" },
    { id: "meta", label: "Meta", icon: "ℹ️" },
  ];

  // ─── LOAD EDIT DATA ──────────────────────────────────────────
  useEffect(() => {
    const driverData = location.state?.driverData;
    const mode = location.state?.mode;
    const driverIdFromState = driverData?.id || location.state?.driverId;

    if (mode === 'edit' && driverIdFromState) {
      setIsEditMode(true);
      setEditId(driverIdFromState);

      const fetchDriver = async () => {
        try {
          const response = await getData(`driver/driver/profile/${driverIdFromState}`);
          if (response.success) {
            const d = response.driver;
            let docVer = d.doc_verification || {};
            ['aadhaar', 'pan', 'dl'].forEach(doc => {
              if (!docVer[doc]) docVer[doc] = { status: 0, remarks: "" };
              if (docVer[doc].status === undefined) docVer[doc].status = 0;
              if (docVer[doc].remarks === undefined) docVer[doc].remarks = "";
            });

            setFormData({
              id: d.id || "", old_id: d.old_id || d.id || "",
              name: d.name || "", first_name: d.first_name || "", last_name: d.last_name || "",
              pre: d.pre || "", gender: d.gender || "",
              contact: d.contact || d.primary_number || "",
              primary_number: d.primary_number || d.contact || "",
              secondary_number: d.secondary_number || "",
              whatsapp_number: d.whatsapp_number || "",
              email: d.email || "", password: "",
              address: d.address || "", permanent_address: d.permanent_address || "",
              city: d.city || "", state: d.state || "", postal: d.postal || "",
              latitude: d.latitude || "", longitude: d.longitude || "",
              date_of_birth: d.date_of_birth || "", blood_group: d.blood_group || "",
              language: d.language || "", father_name: d.father_name || "",
              heighest_education: d.heighest_education || "",
              bank_name: d.bank_name || "", bank_branch: d.bank_branch || "",
              account_holder: d.account_holder || "", account_number: d.account_number || "",
              ifsc: d.ifsc || "", bank_mobile: d.bank_mobile || "", upi: d.upi || "",
              pan_card: d.pan_card || "",
              status: d.status || "", paid: d.paid || "", open_status: d.open_status || "",
              is_ready: d.is_ready || "", hide: d.hide || "",
              wallet: d.wallet || "", cash_inhand: d.cash_inhand || "",
              cash_limit: d.cash_limit || "", cash_limit_cheque: d.cash_limit_cheque || "",
              is_cash_limit_cheque: d.is_cash_limit_cheque || "",
              tshirt_size: d.tshirt_size || "", tshirt_given: d.tshirt_given || "",
              bag_given: d.bag_given || "", starting_point: d.starting_point || "",
              vl_register_number: d.vl_register_number || "", dl_number: d.dl_number || "",
              is_verify_personalinfo: d.is_verify_personalinfo || "",
              is_verify_docinfo: d.is_verify_docinfo || "",
              is_verify_vehicle: d.is_verify_vehicle || "",
              is_verify_bankinfo: d.is_verify_bankinfo || "",
              is_verify_emergency: d.is_verify_emergency || "",
              remarks_personalinfo: d.remarks_personalinfo || "",
              remarks_docinfo: d.remarks_docinfo || "",
              remarks_vehicleinfo: d.remarks_vehicleinfo || "",
              remarks_bankinfo: d.remarks_bankinfo || "",
              remarks_emergencyinfo: d.remarks_emergencyinfo || "",
              doc_verification: docVer,
              family_number_one: d.family_number_one || "",
              family_number_two: d.family_number_two || "",
              subject: d.subject || "", details: d.details || "",
              refer_by: d.refer_by || "", myreferal: d.myreferal || "",
              referral_code: d.referral_code || "",
              aadhaar_number: d.aadhaar_number || "", pan_number: d.pan_number || "",
              created_by_id: d.created_by_id || "",
              created_at: d.created_at || "", updated_at: d.updated_at || "",
              rd: d.rd || "", sessionid: d.sessionid || "",
              login_session: d.login_session || "", fcm_token: d.fcm_token || "",
            });

            setDocuments({
              pic_existing: d.pic || "",
              aadhar_front_existing: d.aadhar_front || "",
              aadhar_back_existing: d.aadhar_back || "",
              pan_card_existing: d.pan_card || "",
              bank_pass_existing: d.bank_pass || "",
              cancel_cheque_existing: d.cancel_cheque || "",
              dl_front_existing: d.dl_front || "",
              dl_back_existing: d.dl_back || "",
              rc_front_existing: d.rc_front || "",
              rc_back_existing: d.rc_back || "",
              resume_existing: d.resume || "",
            });

            const allFilled = {};
            Object.keys(filledSections).forEach(key => { allFilled[key] = true; });
            setFilledSections(allFilled);
          } else {
            alert('Failed to load driver data');
          }
        } catch (err) {
          console.error('Fetch error:', err);
          alert('Could not fetch driver details');
        }
      };
      fetchDriver();
    }
  }, [location.state]);

  // ─── AUTO-SYNC doc_verification → is_verify_docinfo ────────
  useEffect(() => {
    const required = ['aadhaar', 'pan', 'dl'];
    const allVerified = required.every(doc => formData.doc_verification[doc]?.status === 1);
    const newOverall = allVerified ? "1" : "0";
    if (formData.is_verify_docinfo !== newOverall) {
      setFormData(prev => ({ ...prev, is_verify_docinfo: newOverall }));
    }
  }, [formData.doc_verification]);

  // ─── HANDLERS ─────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    checkSectionFilled(activeSection, { ...formData, [name]: value });
  };

  const handleFileUpload = (docName, file) => {
    setDocuments({ ...documents, [docName]: file });
  };

  const checkSectionFilled = (section, data) => {
    let isFilled = false;
    switch (section) {
      case "contact": isFilled = !!data.contact || !!data.email || !!data.city; break;
      case "personalInfo": isFilled = !!data.first_name || !!data.last_name || !!data.name; break;
      case "documents": isFilled = !!documents.aadhar_front || !!documents.aadhar_back || !!documents.pic; break;
      case "bankDetails": isFilled = !!data.bank_name || !!data.account_number; break;
      case "workDuty": isFilled = !!data.status || !!data.open_status || !!data.dl_number; break;
      case "verification": isFilled = !!data.is_verify_personalinfo || !!data.is_verify_docinfo; break;
      case "familyEmergency": isFilled = !!data.family_number_one; break;
      case "meta": isFilled = !!data.details || !!data.subject; break;
      default: isFilled = false;
    }
    setFilledSections(prev => ({ ...prev, [section]: isFilled }));
  };

  const completedCount = Object.values(filledSections).filter(v => v === true).length;
  const progressPercentage = (completedCount / sections.length) * 100;

  // ─── SUBMIT ──────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      const formDataObj = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'doc_verification') {
          formDataObj.append(key, JSON.stringify(formData[key]));
        } else if (key === 'password' && isEditMode && !formData[key]) {
          return;
        } else {
          formDataObj.append(key, formData[key] || '');
        }
      });

      ['pic', 'aadhar_front', 'aadhar_back', 'pan_card', 'bank_pass', 'cancel_cheque', 'dl_front', 'dl_back', 'rc_front', 'rc_back', 'resume'].forEach(field => {
        if (documents[field] instanceof File) {
          formDataObj.append(field, documents[field]);
        }
      });

      const endpoint = isEditMode ? 'driver/update' : 'driver/add';
      const response = await postData(endpoint, formDataObj);

      if (response.success) {
        alert(isEditMode ? 'Driver Updated Successfully!' : 'Driver Created Successfully!');
        onBack ? onBack() : navigate(-1);
      } else {
        alert(response.message || 'Failed to save driver');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert('Network Error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onBack) onBack();
    else navigate(-1);
  };

  // ─── FILE UPLOAD FIELD ──────────────────────────────────────
  const FileUploadField = ({ label, docKey }) => {
    const file = documents[docKey];
    const existingFile = documents[`${docKey}_existing`];
    const getCleanFilename = (picPath) => {
      if (!picPath || picPath.trim() === "" || picPath === "NULL" || picPath === "null") return null;
      if (picPath.includes('/')) return picPath.split('/').pop();
      return picPath;
    };
    const filename = getCleanFilename(existingFile);
    const imageUrl = filename ? `${serverURL}/uploads/${filename}` : null;

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
        <label style={{
          border: `2px dashed ${theme.border}`,
          borderRadius: "12px",
          padding: "14px",
          textAlign: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          background: theme.surfaceLighter,
          width: "100%",
          minHeight: "70px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = theme.primary;
          e.currentTarget.style.background = `${theme.primary}08`;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = theme.border;
          e.currentTarget.style.background = theme.surfaceLighter;
        }}
        >
          <input type="file" style={{ display: "none" }} onChange={(e) => handleFileUpload(docKey, e.target.files[0])} />
          <div style={{
            fontSize: "11px",
            color: theme.textSecondary,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "6px",
            cursor: "pointer",
          }}>
            <span style={{ color: theme.primary }}><UploadIcon /></span>
            <span style={{ wordBreak: "break-all", maxWidth: "140px" }}>
              {file instanceof File ? file.name : (existingFile ? existingFile.split('/').pop() : label)}
            </span>
          </div>
        </label>
        {(file instanceof File || existingFile) && (
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "10px",
            overflow: "hidden",
            border: `1px solid ${theme.border}`,
            flexShrink: 0,
          }}>
            {file instanceof File ? (
              <img src={URL.createObjectURL(file)} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : imageUrl ? (
              <img src={imageUrl} alt={label} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : null}
          </div>
        )}
      </div>
    );
  };

  // ─── SHARED STYLES ──────────────────────────────────────────
  const inputStyle = {
    padding: "10px 14px",
    background: theme.surfaceLighter,
    border: `1px solid ${theme.border}`,
    borderRadius: "10px",
    fontSize: "13px",
    color: theme.textPrimary,
    outline: "none",
    transition: "all 0.2s",
    boxSizing: "border-box",
    width: "100%",
    fontFamily: "inherit",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const textareaStyle = {
    ...inputStyle,
    resize: "vertical",
    minHeight: "80px",
  };

  const labelStyle = {
    fontSize: "11px",
    fontWeight: 700,
    color: theme.textSecondary,
    letterSpacing: "0.3px",
    textTransform: "uppercase",
    marginBottom: "6px",
    display: "block",
  };

  const inputGroupStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  };

  const formGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: "18px",
  };

  const sectionSubtitleStyle = {
    fontSize: "13px",
    fontWeight: 700,
    color: theme.textPrimary,
    marginTop: "24px",
    marginBottom: "14px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${theme.border}`,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  };

  const documentGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: "14px",
  };

  // ─── RENDER FORM SECTIONS ────────────────────────────────────
  const renderForm = () => {
    switch (activeSection) {
      case "contact":
        return (
          <div style={formGridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📞 Contact Number <span style={{ color: theme.error || theme.danger }}>*</span></label>
              <input style={inputStyle} name="contact" value={formData.contact} onChange={handleChange} placeholder="+91 98765 43210"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📱 Primary Number</label>
              <input style={inputStyle} name="primary_number" value={formData.primary_number} onChange={handleChange} placeholder="Primary contact"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📱 Secondary Number</label>
              <input style={inputStyle} name="secondary_number" value={formData.secondary_number} onChange={handleChange} placeholder="Secondary contact"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💬 WhatsApp</label>
              <input style={inputStyle} name="whatsapp_number" value={formData.whatsapp_number} onChange={handleChange} placeholder="WhatsApp number"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>✉️ Email <span style={{ color: theme.error || theme.danger }}>*</span></label>
              <input style={inputStyle} name="email" type="email" value={formData.email} onChange={handleChange} placeholder="driver@example.com"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🔒 Password {!isEditMode && <span style={{ color: theme.error || theme.danger }}>*</span>}</label>
              <input style={inputStyle} name="password" type="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required={!isEditMode}
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🏙️ City</label>
              <input style={inputStyle} name="city" value={formData.city} onChange={handleChange} placeholder="Enter city"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🗺️ State</label>
              <input style={inputStyle} name="state" value={formData.state} onChange={handleChange} placeholder="Enter state"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📍 PIN Code</label>
              <input style={inputStyle} name="postal" value={formData.postal} onChange={handleChange} placeholder="6-digit PIN"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={{ ...inputGroupStyle, gridColumn: "1 / -1" }}>
              <label style={labelStyle}>🏠 Current Address</label>
              <textarea style={textareaStyle} name="address" value={formData.address} onChange={handleChange} placeholder="Current address"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={{ ...inputGroupStyle, gridColumn: "1 / -1" }}>
              <label style={labelStyle}>🏠 Permanent Address</label>
              <textarea style={textareaStyle} name="permanent_address" value={formData.permanent_address} onChange={handleChange} placeholder="Permanent address"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🚩 Starting Point</label>
              <input style={inputStyle} name="starting_point" value={formData.starting_point} onChange={handleChange} placeholder="Starting location"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🌐 Latitude</label>
              <input style={inputStyle} name="latitude" value={formData.latitude} onChange={handleChange} placeholder="Latitude"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🌐 Longitude</label>
              <input style={inputStyle} name="longitude" value={formData.longitude} onChange={handleChange} placeholder="Longitude"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "personalInfo":
        return (
          <div style={formGridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👤 Name</label>
              <input style={inputStyle} name="name" value={formData.name} onChange={handleChange} placeholder="Full name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👤 First Name</label>
              <input style={inputStyle} name="first_name" value={formData.first_name} onChange={handleChange} placeholder="First name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👤 Last Name</label>
              <input style={inputStyle} name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Last name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👤 Prefix</label>
              <select style={selectStyle} name="pre" value={formData.pre} onChange={handleChange}>
                <option value="">Select</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Dr.">Dr.</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👨 Father's Name</label>
              <input style={inputStyle} name="father_name" value={formData.father_name} onChange={handleChange} placeholder="Father's name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🎂 Date of Birth</label>
              <input style={inputStyle} name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange}
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>⚥ Gender</label>
              <select style={selectStyle} name="gender" value={formData.gender} onChange={handleChange}>
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🩸 Blood Group</label>
              <select style={selectStyle} name="blood_group" value={formData.blood_group} onChange={handleChange}>
                <option value="">Select</option>
                <option value="A+">A+</option><option value="A-">A-</option>
                <option value="B+">B+</option><option value="B-">B-</option>
                <option value="O+">O+</option><option value="O-">O-</option>
                <option value="AB+">AB+</option><option value="AB-">AB-</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🌐 Language</label>
              <input style={inputStyle} name="language" value={formData.language} onChange={handleChange} placeholder="Preferred language"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🎓 Highest Education</label>
              <input style={inputStyle} name="heighest_education" value={formData.heighest_education} onChange={handleChange} placeholder="e.g., Graduate"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🆔 Aadhaar Number</label>
              <input style={inputStyle} name="aadhaar_number" value={formData.aadhaar_number} onChange={handleChange} placeholder="12-digit Aadhaar"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📇 PAN Number</label>
              <input style={inputStyle} name="pan_number" value={formData.pan_number} onChange={handleChange} placeholder="ABCDE1234F"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "documents":
        return (
          <>
            <div style={sectionSubtitleStyle}>📸 Profile Picture</div>
            <div style={documentGridStyle}>
              <FileUploadField label="Profile Photo" docKey="pic" />
            </div>
            <div style={sectionSubtitleStyle}>🆔 Aadhaar Card</div>
            <div style={documentGridStyle}>
              <FileUploadField label="Aadhaar Front" docKey="aadhar_front" />
              <FileUploadField label="Aadhaar Back" docKey="aadhar_back" />
            </div>
            <div style={sectionSubtitleStyle}>📇 PAN Card</div>
            <div style={documentGridStyle}>
              <FileUploadField label="PAN Card" docKey="pan_card" />
            </div>
            <div style={sectionSubtitleStyle}>🚗 Driving License</div>
            <div style={documentGridStyle}>
              <FileUploadField label="DL Front" docKey="dl_front" />
              <FileUploadField label="DL Back" docKey="dl_back" />
            </div>
            <div style={sectionSubtitleStyle}>🚛 Vehicle RC</div>
            <div style={documentGridStyle}>
              <FileUploadField label="RC Front" docKey="rc_front" />
              <FileUploadField label="RC Back" docKey="rc_back" />
            </div>
            <div style={sectionSubtitleStyle}>🏦 Bank Documents</div>
            <div style={documentGridStyle}>
              <FileUploadField label="Bank Passbook" docKey="bank_pass" />
              <FileUploadField label="Cancel Cheque" docKey="cancel_cheque" />
            </div>
            <div style={sectionSubtitleStyle}>📄 Resume</div>
            <div style={documentGridStyle}>
              <FileUploadField label="Resume" docKey="resume" />
            </div>
          </>
        );

      case "bankDetails":
        return (
          <div style={formGridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🏦 Bank Name</label>
              <input style={inputStyle} name="bank_name" value={formData.bank_name} onChange={handleChange} placeholder="Bank name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📍 Branch</label>
              <input style={inputStyle} name="bank_branch" value={formData.bank_branch} onChange={handleChange} placeholder="Branch name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👤 Account Holder</label>
              <input style={inputStyle} name="account_holder" value={formData.account_holder} onChange={handleChange} placeholder="Account holder name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🔢 Account Number</label>
              <input style={inputStyle} name="account_number" value={formData.account_number} onChange={handleChange} placeholder="Account number"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🏛️ IFSC</label>
              <input style={inputStyle} name="ifsc" value={formData.ifsc} onChange={handleChange} placeholder="IFSC code"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📱 Bank Mobile</label>
              <input style={inputStyle} name="bank_mobile" value={formData.bank_mobile} onChange={handleChange} placeholder="Registered mobile"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💳 UPI</label>
              <input style={inputStyle} name="upi" value={formData.upi} onChange={handleChange} placeholder="UPI ID"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "workDuty":
        return (
          <div style={formGridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📊 Status</label>
              <select style={selectStyle} name="status" value={formData.status} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💰 Paid</label>
              <select style={selectStyle} name="paid" value={formData.paid} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Paid</option>
                <option value="0">Unpaid</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>⏰ Open Status</label>
              <select style={selectStyle} name="open_status" value={formData.open_status} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">ON</option>
                <option value="0">OFF</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Is Ready</label>
              <select style={selectStyle} name="is_ready" value={formData.is_ready} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🙈 Hide</label>
              <select style={selectStyle} name="hide" value={formData.hide} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👛 Wallet</label>
              <input style={inputStyle} name="wallet" value={formData.wallet} onChange={handleChange} placeholder="Wallet balance"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💵 Cash in Hand</label>
              <input style={inputStyle} name="cash_inhand" value={formData.cash_inhand} onChange={handleChange} placeholder="Cash amount"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💵 Cash Limit</label>
              <input style={inputStyle} name="cash_limit" value={formData.cash_limit} onChange={handleChange} placeholder="Cash limit"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>💵 Cash Limit Cheque</label>
              <input style={inputStyle} name="cash_limit_cheque" value={formData.cash_limit_cheque} onChange={handleChange} placeholder="Cheque limit"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👕 T-Shirt Size</label>
              <select style={selectStyle} name="tshirt_size" value={formData.tshirt_size} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="XS">XS</option><option value="S">S</option>
                <option value="M">M</option><option value="L">L</option>
                <option value="XL">XL</option><option value="XXL">XXL</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👕 T-Shirt Given</label>
              <select style={selectStyle} name="tshirt_given" value={formData.tshirt_given} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🎒 Bag Given</label>
              <select style={selectStyle} name="bag_given" value={formData.bag_given} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🚗 Driving License Number</label>
              <input style={inputStyle} name="dl_number" value={formData.dl_number} onChange={handleChange} placeholder="DL Number"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🚛 Vehicle Registration No.</label>
              <input style={inputStyle} name="vl_register_number" value={formData.vl_register_number} onChange={handleChange} placeholder="Vehicle number"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>🔗 Referral Code</label>
              <input style={inputStyle} name="referral_code" value={formData.referral_code} onChange={handleChange} placeholder="Referral code"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👥 Referred By</label>
              <input style={inputStyle} name="refer_by" value={formData.refer_by} onChange={handleChange} placeholder="Referrer name"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📇 My Referral</label>
              <input style={inputStyle} name="myreferal" value={formData.myreferal} onChange={handleChange} placeholder="My referral code"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "verification":
        return (
          <div style={formGridStyle}>
            {/* Login Status Card */}
            <div style={{
              gridColumn: "1 / -1",
              padding: '20px',
              borderRadius: '14px',
              background: formData.open_status == "1" ? `${theme.success}10` : `${theme.error || theme.danger}10`,
              border: `1px solid ${formData.open_status == "1" ? theme.success : (theme.error || theme.danger)}40`,
              marginBottom: '20px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <label style={{
                    ...labelStyle,
                    color: formData.open_status == "1" ? theme.success : (theme.error || theme.danger),
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '6px',
                  }}>
                    {formData.open_status == "1" ? '🔓 Login Allowed' : '🔒 Login Blocked'}
                  </label>
                  <p style={{ margin: 0, fontSize: '12px', color: theme.textMuted }}>
                    Control whether this user can access the mobile application.
                  </p>
                </div>
                <div style={{
                  display: 'flex',
                  background: theme.surfaceLighter,
                  padding: '4px',
                  borderRadius: '10px',
                  border: `1px solid ${theme.border}`,
                }}>
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'open_status', value: '0' } })}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 700,
                      backgroundColor: formData.open_status == "0" ? (theme.error || theme.danger) : 'transparent',
                      color: formData.open_status == "0" ? '#fff' : theme.textSecondary,
                      transition: 'all 0.2s',
                    }}
                  >
                    OFF (Block)
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChange({ target: { name: 'open_status', value: '1' } })}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 700,
                      backgroundColor: formData.open_status == "1" ? theme.success : 'transparent',
                      color: formData.open_status == "1" ? '#fff' : theme.textSecondary,
                      transition: 'all 0.2s',
                    }}
                  >
                    ON (Allow)
                  </button>
                </div>
              </div>
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Personal Verified</label>
              <select style={selectStyle} name="is_verify_personalinfo" value={formData.is_verify_personalinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Remarks (Personal)</label>
              <input style={inputStyle} name="remarks_personalinfo" value={formData.remarks_personalinfo} onChange={handleChange} placeholder="Remarks"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Documents Verified (Overall)</label>
              <select style={selectStyle} name="is_verify_docinfo" value={formData.is_verify_docinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
              <small style={{ color: theme.textMuted, fontSize: "11px" }}>
                ⚠️ Auto-calculated from Aadhaar, PAN, DL status below.
              </small>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Remarks (Documents)</label>
              <input style={inputStyle} name="remarks_docinfo" value={formData.remarks_docinfo} onChange={handleChange} placeholder="Remarks"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>

            <div style={{ gridColumn: "1 / -1", marginTop: "12px", borderTop: `1px solid ${theme.border}`, paddingTop: "18px" }}>
              <h4 style={{ fontSize: "14px", fontWeight: 700, color: theme.textPrimary, marginBottom: "14px" }}>
                📄 Document-wise Verification (Aadhaar, PAN, DL)
              </h4>
              {["aadhaar", "pan", "dl"].map((doc) => {
                const docData = formData.doc_verification?.[doc] || { status: 0, remarks: "" };
                return (
                  <div key={doc} style={{ ...inputGroupStyle, marginBottom: "14px" }}>
                    <label style={labelStyle}>{doc.toUpperCase()}</label>
                    <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                      <select
                        style={{ ...selectStyle, flex: 1, minWidth: "120px" }}
                        value={String(docData.status)}
                        onChange={(e) => {
                          const newDocVer = { ...formData.doc_verification };
                          if (!newDocVer[doc]) newDocVer[doc] = {};
                          newDocVer[doc].status = parseInt(e.target.value);
                          setFormData({ ...formData, doc_verification: newDocVer });
                        }}
                      >
                        <option value="0">Pending</option>
                        <option value="1">Verified</option>
                        <option value="2">Rejected</option>
                      </select>
                      <input
                        style={{ ...inputStyle, flex: 2 }}
                        placeholder="Remarks"
                        value={docData.remarks || ""}
                        onChange={(e) => {
                          const newDocVer = { ...formData.doc_verification };
                          if (!newDocVer[doc]) newDocVer[doc] = {};
                          newDocVer[doc].remarks = e.target.value;
                          setFormData({ ...formData, doc_verification: newDocVer });
                        }}
                        onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                        onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
                      />
                      <span style={{
                        fontSize: "11px",
                        color: docData.status === 1 ? theme.success : docData.status === 2 ? (theme.error || theme.danger) : theme.textMuted,
                        minWidth: "70px",
                        fontWeight: 600,
                      }}>
                        {["Pending","Verified","Rejected"][docData.status] || "Pending"}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: "11px", color: theme.textMuted, marginTop: "8px" }}>
                ⚠️ All three documents must be Verified for overall "Document Verified" to auto-update.
              </div>
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Vehicle Verified</label>
              <select style={selectStyle} name="is_verify_vehicle" value={formData.is_verify_vehicle} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Remarks (Vehicle)</label>
              <input style={inputStyle} name="remarks_vehicleinfo" value={formData.remarks_vehicleinfo} onChange={handleChange} placeholder="Remarks"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Bank Verified</label>
              <select style={selectStyle} name="is_verify_bankinfo" value={formData.is_verify_bankinfo} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Remarks (Bank)</label>
              <input style={inputStyle} name="remarks_bankinfo" value={formData.remarks_bankinfo} onChange={handleChange} placeholder="Remarks"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>✅ Emergency Verified</label>
              <select style={selectStyle} name="is_verify_emergency" value={formData.is_verify_emergency} onChange={handleChange}>
                <option value="">Select...</option>
                <option value="1">Verified</option>
                <option value="0">Pending</option>
                <option value="2">Rejected</option>
              </select>
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Remarks (Emergency)</label>
              <input style={inputStyle} name="remarks_emergencyinfo" value={formData.remarks_emergencyinfo} onChange={handleChange} placeholder="Remarks"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "familyEmergency":
        return (
          <div style={formGridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👨‍👩‍👧 Family Number 1</label>
              <input style={inputStyle} name="family_number_one" value={formData.family_number_one} onChange={handleChange} placeholder="Emergency contact 1"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>👨‍👩‍👧 Family Number 2</label>
              <input style={inputStyle} name="family_number_two" value={formData.family_number_two} onChange={handleChange} placeholder="Emergency contact 2"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
          </div>
        );

      case "meta":
        return (
          <>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>📝 Subject</label>
              <input style={inputStyle} name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={{ ...inputGroupStyle, gridColumn: "1 / -1" }}>
              <label style={labelStyle}>📝 Details / Remarks</label>
              <textarea style={textareaStyle} name="details" value={formData.details} onChange={handleChange} placeholder="Additional details"
                onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
              />
            </div>
            <div style={{ marginTop: "24px", gridColumn: "1 / -1" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "12px 0",
                borderBottom: `1px solid ${theme.border}`,
              }}>
                <div style={{ ...labelStyle, width: "120px", marginBottom: 0 }}>🆔 ID</div>
                <div style={{ fontSize: "13px", color: theme.textPrimary, flex: 1, fontFamily: "monospace" }}>
                  {formData.id || "New Driver"}
                </div>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "12px 0",
                borderBottom: `1px solid ${theme.border}`,
              }}>
                <div style={{ ...labelStyle, width: "120px", marginBottom: 0 }}>📅 Created At</div>
                <div style={{ fontSize: "13px", color: theme.textPrimary, flex: 1 }}>
                  {formData.created_at || "Now"}
                </div>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "12px 0",
                borderBottom: `1px solid ${theme.border}`,
              }}>
                <div style={{ ...labelStyle, width: "120px", marginBottom: 0 }}>👤 Created By</div>
                <div style={{ flex: 1 }}>
                  <input style={inputStyle} name="created_by_id" value={formData.created_by_id} onChange={handleChange} placeholder="Admin ID"
                    onFocus={(e) => { e.currentTarget.style.borderColor = theme.primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = theme.border; e.currentTarget.style.boxShadow = "none"; }}
                  />
                </div>
              </div>
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "12px 0",
              }}>
                <div style={{ ...labelStyle, width: "120px", marginBottom: 0 }}>🔄 Session ID</div>
                <div style={{ fontSize: "13px", color: theme.textPrimary, flex: 1, fontFamily: "monospace" }}>
                  {formData.sessionid || "—"}
                </div>
              </div>
            </div>
          </>
        );
      default: return null;
    }
  };

  const currentIndex = sections.findIndex(s => s.id === activeSection);
  const isLastSection = currentIndex === sections.length - 1;
  const isFirstSection = currentIndex === 0;
  const goToNext = () => { if (currentIndex < sections.length - 1) setActiveSection(sections[currentIndex + 1].id); };
  const goToPrev = () => { if (currentIndex > 0) setActiveSection(sections[currentIndex - 1].id); };

  return (
    <div style={{
      background: theme.background,
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      transition: "background 0.3s ease",
    }}>
      <style>{`
        input::placeholder, textarea::placeholder, select:invalid { color: ${theme.textMuted}; opacity: 0.7; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>

      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "16px",
        padding: "16px 24px",
        borderBottom: `1px solid ${theme.border}`,
        background: theme.surface,
        position: "sticky",
        top: 0,
        zIndex: 10,
      }}>
        <button
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: theme.textSecondary,
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "13px",
            padding: "8px 12px",
            borderRadius: "8px",
            transition: "all 0.2s",
          }}
          onClick={handleCancel}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.surfaceLighter;
            e.currentTarget.style.color = theme.textPrimary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
            e.currentTarget.style.color = theme.textSecondary;
          }}
        >
          <BackIcon /> Back
        </button>
        <h2 style={{
          fontSize: "18px",
          fontWeight: 700,
          color: theme.textPrimary,
          margin: 0,
          flex: 1,
        }}>
          {isEditMode ? "Edit Driver" : "Add New Driver"}
        </h2>
        <div style={{ width: "80px" }} />
      </div>

      {/* Progress Bar */}
      <div style={{
        padding: "14px 24px",
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "8px",
        }}>
          <span style={{ fontSize: "12px", fontWeight: 600, color: theme.textMuted }}>
            Form Completion
          </span>
          <span style={{ fontSize: "12px", fontWeight: 700, color: theme.primary }}>
            {Math.round(progressPercentage)}%
          </span>
        </div>
        <div style={{
          background: theme.surfaceLighter,
          borderRadius: "10px",
          height: "6px",
          overflow: "hidden",
          border: `1px solid ${theme.border}`,
        }}>
          <div style={{
            background: `linear-gradient(90deg, ${theme.primary}, ${theme.primary}cc)`,
            width: `${progressPercentage}%`,
            height: "100%",
            borderRadius: "10px",
            transition: "width 0.3s ease",
          }} />
        </div>
      </div>

      {/* Section Tabs */}
      <div style={{
        display: "flex",
        gap: "8px",
        padding: "14px 24px",
        overflowX: "auto",
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        scrollbarWidth: "thin",
      }}>
        {sections.map((section) => (
          <button
            key={section.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "10px 16px",
              borderRadius: "10px",
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s",
              background: activeSection === section.id ? theme.primary : theme.surfaceLighter,
              border: activeSection === section.id ? `1px solid ${theme.primary}` : `1px solid ${theme.border}`,
              color: activeSection === section.id ? "#fff" : theme.textSecondary,
              whiteSpace: "nowrap",
              boxShadow: activeSection === section.id ? `0 4px 12px ${theme.primary}40` : "none",
            }}
            onClick={() => setActiveSection(section.id)}
            onMouseEnter={(e) => {
              if (activeSection !== section.id) {
                e.currentTarget.style.background = theme.surface;
                e.currentTarget.style.borderColor = theme.primary;
              }
            }}
            onMouseLeave={(e) => {
              if (activeSection !== section.id) {
                e.currentTarget.style.background = theme.surfaceLighter;
                e.currentTarget.style.borderColor = theme.border;
              }
            }}
          >
            <span>{section.icon}</span>
            <span>{section.label}</span>
            {filledSections[section.id] && (
              <span style={{
                width: "18px",
                height: "18px",
                borderRadius: "50%",
                background: activeSection === section.id ? "rgba(255,255,255,0.2)" : `${theme.success}20`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: activeSection === section.id ? "#fff" : theme.success,
              }}>
                <CheckCircleIcon />
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Form Container */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "24px",
      }}>
        <div style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: "16px",
          padding: "28px",
          boxShadow: `0 4px 12px ${theme.primary}08`,
        }}>
          <div style={{
            fontSize: "18px",
            fontWeight: 700,
            color: theme.textPrimary,
            marginBottom: "24px",
            paddingBottom: "14px",
            borderBottom: `1px solid ${theme.border}`,
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}>
            <span>{sections.find(s => s.id === activeSection)?.icon}</span>
            {sections.find(s => s.id === activeSection)?.label}
          </div>
          <form onSubmit={handleSubmit}>
            {renderForm()}
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: "28px",
              paddingTop: "20px",
              borderTop: `1px solid ${theme.border}`,
            }}>
              <button
                type="button"
                style={{
                  padding: "10px 20px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: isFirstSection ? "not-allowed" : "pointer",
                  border: `1px solid ${theme.border}`,
                  background: theme.surfaceLighter,
                  color: isFirstSection ? theme.textMuted : theme.textSecondary,
                  opacity: isFirstSection ? 0.5 : 1,
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onClick={goToPrev}
                disabled={isFirstSection}
              >
                <ChevronLeftIcon /> Previous
              </button>
              {!isLastSection ? (
                <button
                  type="button"
                  style={{
                    padding: "10px 24px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: theme.primary,
                    border: "none",
                    color: "#fff",
                    boxShadow: `0 4px 12px ${theme.primary}40`,
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onClick={goToNext}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}40`;
                  }}
                >
                  Next <ChevronRightIcon />
                </button>
              ) : (
                <button
                  type="submit"
                  style={{
                    padding: "10px 24px",
                    borderRadius: "10px",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    background: theme.success,
                    border: "none",
                    color: "#fff",
                    boxShadow: `0 4px 12px ${theme.success}40`,
                    transition: "all 0.2s",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = `0 6px 16px ${theme.success}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 4px 12px ${theme.success}40`;
                  }}
                >
                  <SaveIcon /> {isEditMode ? "Update Driver" : "Create Driver"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Fixed Bottom Buttons */}
      <div style={{
        position: "sticky",
        bottom: 0,
        padding: "14px 24px",
        background: theme.surface,
        borderTop: `1px solid ${theme.border}`,
        display: "flex",
        justifyContent: "flex-end",
        gap: "12px",
        zIndex: 10,
        boxShadow: `0 -4px 12px ${theme.primary}08`,
      }}>
        <button
          style={{
            padding: "10px 22px",
            background: "transparent",
            border: `1px solid ${theme.border}`,
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            color: theme.textSecondary,
            cursor: "pointer",
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onClick={handleCancel}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = theme.surfaceLighter;
            e.currentTarget.style.borderColor = theme.error || theme.danger;
            e.currentTarget.style.color = theme.error || theme.danger;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = theme.border;
            e.currentTarget.style.color = theme.textSecondary;
          }}
        >
          <XIcon /> Cancel
        </button>
        <button
          style={{
            padding: "10px 24px",
            background: loading ? theme.textMuted : theme.primary,
            border: "none",
            borderRadius: "10px",
            fontSize: "13px",
            fontWeight: 600,
            color: "#fff",
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: loading ? "none" : `0 4px 12px ${theme.primary}40`,
            transition: "all 0.2s",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onClick={handleSubmit}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = loading ? "none" : `0 4px 12px ${theme.primary}40`;
          }}
        >
          {loading ? (
            <>
              <div style={{
                width: "12px",
                height: "12px",
                border: "2px solid rgba(255,255,255,0.3)",
                borderTopColor: "#fff",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
              }} />
              Saving...
            </>
          ) : (
            <>
              <SaveIcon /> {isEditMode ? "Update" : "Create"}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AddNewDriver;